/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import './types'

import type { AppInitialProps } from 'next/app'
import * as React from 'react'

import type { NextPageAnalytics } from './types'
import { getClient, isBlank } from '.'

export interface PageRouterTrackingProps extends AppInitialProps {
  analytics?: NextPageAnalytics | null
  Component: any
}

export interface AppRouterTrackingProps {
  name: string
  customParameters?: Record<string, unknown>
  disableTracking?: boolean
}

const isAppRouterTrackingProps = (
  props: PageRouterTrackingProps | AppRouterTrackingProps,
): props is AppRouterTrackingProps => {
  return 'name' in props
}
export function getAnalyticsFromPage(
  props: PageRouterTrackingProps,
): NextPageAnalytics {
  const analytics = props.Component.analytics
  const logError = () => {
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        `Page analytics missing for component '${props.Component.name}'.\nTry adding '${props.Component.name}.analytics = { name: <screen name> }' to your component`,
      )
    }
  }
  if (analytics == null) {
    logError()
    return {
      name: '',
      customParameters: {},
    }
  }

  const {
    name,
    parameters: customParameters,
    disableTracking,
  } = typeof analytics === 'function' ? analytics(props) : analytics

  if (disableTracking) {
    return {
      name: undefined,
      customParameters: {},
      disableTracking,
    }
  }

  if (isBlank(name)) {
    logError()
    return {
      name: undefined,
      customParameters: {},
      disableTracking,
    }
  }

  return {
    name,
    customParameters,
    disableTracking,
  }
}

const screenTrackEventBacklog: Set<{
  name: string | Promise<string> | undefined
  stringifiedParams: string
  disableTracking: boolean | undefined
}> = new Set()

export async function sendNextScreenTrackingBacklog() {
  const analyticsClient = getClient()

  // Depending on implementation, the analytics client may not support backlogging
  if (analyticsClient?.options?.enableBacklog === true) {
    for (const screenTrackEvent of screenTrackEventBacklog) {
      const { name, stringifiedParams, disableTracking } = screenTrackEvent

      if (analyticsClient == null) {
        continue
      }

      if (name == null || disableTracking === true) {
        screenTrackEventBacklog.delete(screenTrackEvent)
        continue
      }

      const resolvedName = await name

      if (resolvedName == null || resolvedName === '') {
        if (process.env.NODE_ENV !== 'production') {
          console.error(
            'Screen tracking failed: name is undefined or empty after resolving',
          )
        }
        screenTrackEventBacklog.delete(screenTrackEvent)
        continue
      }

      analyticsClient.trackScreen({
        name: resolvedName,
        customParameters: JSON.parse(stringifiedParams),
      })
      screenTrackEventBacklog.delete(screenTrackEvent)
    }
  }
}

export function useNextScreenTracking(
  props: PageRouterTrackingProps | AppRouterTrackingProps,
) {
  const analyticsClient = getClient()
  let name: string | Promise<string> | undefined
  let customParameters: Record<string, unknown> | undefined
  let disableTracking: boolean | undefined

  if (isAppRouterTrackingProps(props)) {
    name = props.name
    customParameters = props.customParameters
    disableTracking = props.disableTracking
  } else {
    const pageRouterTrackingProps = getAnalyticsFromPage(props)
    name = pageRouterTrackingProps.name
    customParameters = pageRouterTrackingProps.customParameters
    disableTracking = pageRouterTrackingProps.disableTracking
  }

  // We're stringifying the parameters so that the following 'useEffect' does a comparison
  // between object values. Otherwise the effect always re-runs, even when parameters don't
  // change, because the object reference has changed.
  const stringifiedParams = JSON.stringify(customParameters ?? {})

  React.useEffect(() => {
    if (analyticsClient == null) {
      screenTrackEventBacklog.add({ name, stringifiedParams, disableTracking })
    }
    if (analyticsClient == null || name == null || disableTracking === true) {
      return
    }

    const trackThatScreen = async () => {
      const resolvedName = await name
      if (resolvedName == null || resolvedName === '') {
        if (process.env.NODE_ENV !== 'production') {
          console.error(
            'Screen tracking failed: name is undefined or empty after resolving',
          )
        }
        return
      }
      analyticsClient.trackScreen({
        name: resolvedName,
        customParameters: JSON.parse(stringifiedParams),
      })
    }

    sendNextScreenTrackingBacklog()

    trackThatScreen()
  }, [name, stringifiedParams, disableTracking, analyticsClient])
}
